/**
 * integrityDisplay.tsx — shared presentational pieces for App Integrity:
 * the per-check results table and status-chip color mapping.
 */
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, Functions, HighlightOff } from '@mui/icons-material';
import type {
  IntegrityCheck,
  IntegrityColumnChecksum,
  IntegrityFormulaLink,
} from '../../../services/appIntegrityService';

export function statusChipColor(
  status: string,
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
      return 'error';
    case 'error':
      return 'warning';
    case 'running':
      return 'info';
    default:
      return 'default';
  }
}

function formatTolerance(check: IntegrityCheck): string {
  const parts: string[] = [];
  if (check.toleranceAbs !== null) parts.push(`±${check.toleranceAbs}`);
  if (check.toleranceRel !== null) parts.push(`±${check.toleranceRel * 100}% rel`);
  return parts.length ? parts.join(' / ') : 'default';
}

/** Hover card: the Formula-Registry entry behind a checked value
 *  (Vahid 2026-07-22 — reviewers see WHICH formula produced the number). */
const FormulaTip = ({ link }: { link: IntegrityFormulaLink }) => (
  <Box sx={{ maxWidth: 420 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{link.formulaLabel}</Typography>
    {link.kind === 'raw' ? (
      <Typography variant="body2">Measured value (raw input — no formula).</Typography>
    ) : (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {link.formula}
      </Typography>
    )}
    {link.note && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {link.note}
      </Typography>
    )}
  </Box>
);

export const ChecksTable = ({
  checks,
  formulas,
}: {
  checks: IntegrityCheck[];
  /** Formula-registry links keyed by check path (optional — older payloads). */
  formulas?: Record<string, IntegrityFormulaLink>;
}) => {
  if (!checks.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No checks were evaluated.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={40} />
          <TableCell>Check</TableCell>
          <TableCell align="right">Expected</TableCell>
          <TableCell align="right">Actual</TableCell>
          <TableCell align="right">Δ</TableCell>
          <TableCell align="right">Tolerance</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {checks.map((check) => (
          <TableRow key={check.path} hover>
            <TableCell>
              {check.pass ? (
                <CheckCircleOutline fontSize="small" color="success" />
              ) : (
                <HighlightOff fontSize="small" color="error" />
              )}
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2">{check.label}</Typography>
                {formulas?.[check.path] && (
                  <Tooltip title={<FormulaTip link={formulas[check.path]} />} placement="right" arrow>
                    <Functions
                      fontSize="inherit"
                      sx={{ color: 'text.secondary', fontSize: '0.95rem', cursor: 'help' }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {check.path}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {check.expected}
                {check.unit ? ` ${check.unit}` : ''}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace' }}
                color={check.pass ? 'text.primary' : 'error'}
              >
                {check.actual ?? '—'}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {check.delta === null || check.delta === 0
                  ? check.delta === 0
                    ? '0'
                    : '—'
                  : check.delta.toExponential(2)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Chip size="small" variant="outlined" label={formatTolerance(check)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/** Calculated-column checksums (2026-07-24): a compact digest of every
 *  calculated column's full data captured with the run — the audit trail's
 *  proof that the column data behind the checked summary values hasn't
 *  drifted. Column names are the ACTUAL stored field names so they can be
 *  matched to the on-screen grid; the tooltip documents the recipe. */
export const ColumnChecksumsTable = ({ checksums }: { checksums?: IntegrityColumnChecksum[] }) => {
  if (!checksums?.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Tooltip
        title="Column sum = plain total of the column's numeric values (re-derive it by hand or in Excel). SHA-256 = hash over the column's stored values in row order (null → empty string), joined with '|'; first 16 hex digits shown. The sum is human-checkable; the hash is tamper-evident."
        placement="right"
        arrow
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, cursor: 'help', display: 'inline-block' }}>
          Calculated-column checksums
        </Typography>
      </Tooltip>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data set</TableCell>
            <TableCell>Column (as stored)</TableCell>
            <TableCell align="right">Rows</TableCell>
            <TableCell align="right">Column sum</TableCell>
            <TableCell>This run (SHA-256, first 16)</TableCell>
            <TableCell>Golden</TableCell>
            <TableCell>Match</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checksums.map((c) => (
            <TableRow key={`${c.scope}:${c.column}`} hover>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{c.scope}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.column}</Typography>
                {c.label && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {c.label}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{c.rows}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {c.sum ?? '—'}
                </Typography>
                {c.goldenSum !== undefined && c.goldenSum !== null && c.goldenSum !== c.sum && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', fontFamily: 'monospace' }}>
                    golden: {c.goldenSum}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.checksum}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                  {c.golden ?? '—'}
                </Typography>
                {c.goldenSum !== undefined && c.goldenSum !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                    sum {c.goldenSum}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {c.golden === undefined ? (
                  <Tooltip title="No golden pinned yet — the next suite run (or a snapshot update) pins this column's baseline">
                    <Chip size="small" variant="outlined" label="NOT PINNED" />
                  </Tooltip>
                ) : c.matchesGolden ? (
                  <Chip size="small" color="success" label="PASS" />
                ) : (
                  <Tooltip title="This run's column data differs from the pinned golden baseline">
                    <Chip size="small" color="error" label="DIFF" />
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
